import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportOldAffiliateCommissionsByProductData,
} from '@src/types/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { toast } from 'react-toastify';
import { useProducts } from '@src/hooks/useProducts';
import { useDisplayedProductsForTableAndChart } from '@src/hooks/useDisplayedProductsForTableAndChart';
import { buildCommonPayloadWithGroupingAndProducts } from '@src/utils/buildCommonPayload';
import {
  exportOldAffiliateCommissionsByProduct,
  getOldAffiliateCommissionsByProduct,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const MAX_CHART_POINTS = 400;
const INITIAL_TABLE_ROWS = 300;
const TABLE_ROWS_STEP = 300;

const downsampleRows = (
  rows: TReportOldAffiliateCommissionsByProductData[],
  maxPoints: number
): TReportOldAffiliateCommissionsByProductData[] => {
  if (rows.length <= maxPoints) {
    return rows;
  }

  const step = Math.ceil(rows.length / maxPoints);
  const sampled = rows.filter((_, index) => index % step === 0);

  if (sampled[sampled.length - 1] !== rows[rows.length - 1]) {
    sampled.push(rows[rows.length - 1]);
  }

  return sampled;
};

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const OldAffiliateCommissionsByProduct: FC = () => {
  // Data state
  const [data, setData] = useState<
    TReportOldAffiliateCommissionsByProductData[]
  >([]);
  const [tableRowsLimit, setTableRowsLimit] = useState(INITIAL_TABLE_ROWS);
  const [isLoadingMoreRows, setIsLoadingMoreRows] = useState(false);
  const { products } = useProducts();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { selectedItemsForDisplay } = useDisplayedProductsForTableAndChart(
    products,
    filters.selectedProducts
  );

  const sampledChartSource = useMemo(
    () => downsampleRows(data, MAX_CHART_POINTS),
    [data]
  );

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: selectedItemsForDisplay,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products?.[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
    itemsField: 'products',
    includeTotalsRow: true,
    valueFormatter: (_key: string, value: number) =>
      `$${value.toLocaleString()}`,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data: sampledChartSource,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products?.[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
  });

  const handleRunReport = useCallback(async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await getOldAffiliateCommissionsByProduct(payload);

      const productLabelById = products.reduce(
        (acc, product) => {
          acc[product.value] = product.label;

          return acc;
        },
        {} as Record<string, string>
      );

      const mapPackageKeysToLabels = (
        source?: Record<string, number>
      ): Record<string, number> => {
        if (!source) {
          return {};
        }

        return Object.entries(source).reduce(
          (acc, [packageId, value]) => {
            const packageLabel =
              productLabelById[packageId] || `Package #${packageId}`;

            acc[packageLabel] = value;

            return acc;
          },
          {} as Record<string, number>
        );
      };

      const mappedData = response.map((item) => ({
        ...item,
        products: mapPackageKeysToLabels(item.products),
        products_previous: mapPackageKeysToLabels(item.products_previous),
        products_previous_year: mapPackageKeysToLabels(
          item.products_previous_year
        ),
      }));

      setData(mappedData);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to generate report');
    } finally {
      filters.setIsLoading(false);
    }
  }, [filters, products]);

  const visibleTableData = useMemo(() => {
    if (tableData.length === 0) {
      return tableData;
    }

    const totalsRow = tableData[tableData.length - 1];
    const normalRows = tableData.slice(0, -1);
    const limitedRows = normalRows.slice(0, tableRowsLimit);

    if (totalsRow) {
      return [...limitedRows, totalsRow];
    }

    return limitedRows;
  }, [tableData, tableRowsLimit]);

  const totalRows = Math.max(tableData.length - 1, 0);
  const visibleRows = Math.min(tableRowsLimit, totalRows);
  const hasMoreRows = visibleRows < totalRows;

  const handleLoadMoreRows = useCallback(async () => {
    if (isLoadingMoreRows) {
      return;
    }

    setIsLoadingMoreRows(true);

    // Yield to the browser before expanding rows so button/loading state paints first.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve());
      });
    });

    setTableRowsLimit((prev) => Math.min(prev + TABLE_ROWS_STEP, totalRows));
    setIsLoadingMoreRows(false);
  }, [isLoadingMoreRows, totalRows]);

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGroupingAndProducts(filters);
      const response = await exportOldAffiliateCommissionsByProduct(payload);

      downloadFile(response.data, 'old-affiliate-commissions-by-product');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport().then(() => null);
  }, []);

  useEffect(() => {
    setTableRowsLimit(INITIAL_TABLE_ROWS);
    setIsLoadingMoreRows(false);
  }, [data.length]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Old Affiliate Commissions by Product
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showProductsFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      {data.length > 0 && (
        <>
          <Card className="p-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">
                Commission Trends
              </h2>
              <ResponsiveContainer height={400} width="100%">
                <LineChart data={chartData}>
                  <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    stroke="#999"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis
                    stroke="#999"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    iconType="line"
                    wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                  />

                  {/* Main data lines */}
                  {displayItems.map((product, index) => (
                    <Line
                      key={product}
                      activeDot={{ r: 6 }}
                      dataKey={product}
                      dot={{ fill: getChartColor(index), r: 4 }}
                      name={product}
                      stroke={getChartColor(index)}
                      strokeWidth={2}
                      type="monotone"
                    />
                  ))}

                  {/* Previous period lines */}
                  {filters.compareToPreviousPeriod &&
                    displayItems.map((product, index) => (
                      <Line
                        key={`${product}_previous_period`}
                        activeDot={{ r: 4 }}
                        dataKey={`${product}_previous_period`}
                        dot={{
                          fill: getChartColor(index),
                          r: 3,
                        }}
                        name={`${product} (Previous Period)`}
                        stroke={getChartColor(index)}
                        strokeDasharray="5 5"
                        strokeWidth={1.5}
                        type="monotone"
                      />
                    ))}

                  {/* Previous year lines */}
                  {filters.compareToSamePeriodPreviousYear &&
                    displayItems.map((product, index) => (
                      <Line
                        key={`${product}_previous_year`}
                        activeDot={{ r: 4 }}
                        dataKey={`${product}_previous_year`}
                        dot={{
                          fill: getChartColor(index),
                          r: 3,
                        }}
                        name={`${product} (Previous Year)`}
                        stroke={getChartColor(index)}
                        strokeDasharray="2 2"
                        strokeWidth={1.5}
                        type="monotone"
                      />
                    ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-6">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-white">
                Detailed Data
              </h2>
              <div className="w-full overflow-x-auto">
                <div
                  style={{
                    minWidth: `${Math.max(columns.length * 180, 1000)}px`,
                  }}
                >
                  <RadixTable columns={columns} data={visibleTableData} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-3 text-xs text-[#999]">
                <p>
                  Showing {visibleRows.toLocaleString()} /{' '}
                  {totalRows.toLocaleString()} rows
                </p>
                {hasMoreRows && (
                  <Button
                    className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                    disabled={isLoadingMoreRows}
                    variant="outline"
                    onClick={handleLoadMoreRows}
                  >
                    {isLoadingMoreRows ? 'Loading rows...' : 'Load more rows'}
                  </Button>
                )}
              </div>
              <Divider className="my-4" />
              <div className="flex justify-end">
                <Button
                  className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                  variant="outline"
                  onClick={handleExportReport}
                >
                  Download CSV
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
